import { NavLink, useNavigate } from "react-router-dom";
import { LogOut, ReceiptText, ScanBarcode, Package, Settings2, Tag, Users, Boxes, Undo2, Store, ShoppingBag, BarChart3, LayoutGrid, ChefHat, UtensilsCrossed, CalendarClock, Home } from "lucide-react";
import { useNewOrders } from "@/pages/OnlineOrders";
import { BranchSwitcher } from "@/components/BranchSwitcher";
import { useAuth } from "@/context/AuthContext";
import { exitImpersonation } from "@/lib/api";
import { DemoCountdown } from "@/components/DemoCountdown";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { ScanLoginButton } from "@/components/ScanLoginButton";

const NAV = [
  { to: "/pos", label: "Register", icon: ScanBarcode, id: "pos" },
  { to: "/sales", label: "Sales", icon: ReceiptText, id: "sales", perm: "sales_history" },
  { to: "/products", label: "Products", icon: Package, id: "products", perm: "products" },
  { to: "/price-tags", label: "Tags", icon: Tag, id: "price-tags", perm: "products" },
  { to: "/returns", label: "Returns", icon: Undo2, id: "returns", perm: "returns" },
  { to: "/inventory", label: "Inventory", icon: Boxes, id: "inventory", inv: true },
  { to: "/online-store", label: "Online Store", icon: Store, id: "online-store", perm: "online_orders", premium: true },
  { to: "/online-orders", label: "Orders", icon: ShoppingBag, id: "online-orders", perm: "online_orders", badge: true },
  { to: "/reports", label: "Reports", icon: BarChart3, id: "reports", perm: "reports", premium: true },
  { to: "/team", label: "Team", icon: Users, id: "team", owner: true },
  { to: "/settings", label: "Settings", icon: Settings2, id: "settings" },
];

const REST_NAV = [
  { to: "/restaurant", label: "Home", icon: Home, id: "restaurant", any: ["take_orders", "billing", "reports", "menu", "tables"] },
  { to: "/tables", label: "Tables", icon: LayoutGrid, id: "tables", any: ["take_orders", "billing", "tables"] },
  { to: "/kitchen", label: "Kitchen", icon: ChefHat, id: "kitchen", perm: "kitchen" },
  { to: "/reservations", label: "Reservations", icon: CalendarClock, id: "reservations", perm: "reservations" },
  { to: "/menu", label: "Menu", icon: UtensilsCrossed, id: "menu", perm: "menu" },
  { to: "/online-store", label: "Online Ordering", icon: Store, id: "online-store", perm: "online_orders", premium: true },
  { to: "/online-orders", label: "Orders", icon: ShoppingBag, id: "online-orders", perm: "online_orders", badge: true },
  { to: "/sales", label: "Sales", icon: ReceiptText, id: "sales", perm: "sales_history" },
  { to: "/reports", label: "Reports", icon: BarChart3, id: "reports", perm: "reports", premium: true },
  { to: "/team", label: "Team", icon: Users, id: "team", owner: true },
  { to: "/settings", label: "Settings", icon: Settings2, id: "settings", perm: "settings" },
];

export function LogoMark({ className = "h-9 w-9" }) {
  return (
    <svg viewBox="0 0 100 100" className={className} role="img" aria-label="Smart-in Retail logo" data-testid="logo-mark">
      <rect width="100" height="100" rx="22" fill="#0F172A" />
      <path transform="translate(0 2)" d="M65.5 20.5C62.5 17.3 58.5 16 53.5 16C46.5 16 42 19.6 42 24C42 28.6 46.6 29.6 53.6 30.5C61.2 31.5 66.2 33.8 66.2 38.3C66.2 43 60.6 45.3 53.4 45.3C47.4 45.3 42.4 43.3 39.2 39.8" fill="none" stroke="#FFFFFF" strokeWidth="9.2" strokeLinejoin="round" />
      <g fill="none" stroke="#F59E0B" strokeWidth="4.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M13.5 27.5L22.5 29.6L32 67.5" />
        <path d="M32.5 68.6L75 62.6L79.6 37.8" />
        <path d="M32.5 68.6C27.4 69.2 27.4 75.4 32.6 75.4L74.4 75.4" />
      </g>
      <circle cx="40.6" cy="84.2" r="4.1" fill="#F59E0B" />
      <circle cx="65.4" cy="84.2" r="4.1" fill="#F59E0B" />
    </svg>
  );
}

export function BrandMark({ light = true }) {
  return (
    <div className="flex items-center gap-2.5" data-testid="brand-mark">
      <LogoMark className={`h-9 w-9 shrink-0 ${light ? "rounded-[10px] ring-1 ring-white/15" : ""}`} />
      <div className="leading-none">
        <div className={`whitespace-nowrap font-display text-[17px] font-bold tracking-tight ${light ? "text-white" : "text-slate-900"}`}>Smart-in</div>
        <div className="font-mono text-[9px] uppercase tracking-[0.28em] text-amber-400">Retail</div>
      </div>
    </div>
  );
}

export default function AppShell({ children }) {
  const { session, signOut } = useAuth();
  const nav = useNavigate();
  const biz = session.business;
  const wide = useMediaQuery("(min-width: 1024px)");
  const isOwner = session.user.role === "owner";
  const newOrders = useNewOrders();
  return (
    <div className="flex h-[100dvh] flex-col bg-slate-50">
      {session.impersonation && (
        <div className="flex h-9 shrink-0 items-center justify-center gap-3 bg-amber-500 px-3 text-sm font-bold text-slate-950" data-testid="impersonation-banner">
          <span className="truncate">Super admin view — {biz.name}</span>
          <button onClick={exitImpersonation} data-testid="impersonation-exit-button" className="h-7 rounded-md bg-slate-950 px-3 text-xs font-bold text-white hover:bg-slate-800">Exit</button>
        </div>
      )}
      <header className="grain relative z-30 flex h-14 shrink-0 items-center gap-3 bg-slate-950 px-3 sm:px-5" data-testid="app-header">
        <div className="hidden sm:block"><BrandMark /></div>
        <nav className="ml-1 flex min-w-0 flex-1 items-center gap-0.5 overflow-x-auto [scrollbar-width:none] sm:ml-4 sm:gap-1 [&::-webkit-scrollbar]:hidden" data-testid="main-nav">
          {(session.business.business_type === "restaurant" ? REST_NAV : NAV).filter((n) => (n.any ? n.any.some((p) => session.permissions?.[p]) : n.inv ? session.business.inventory_entitled && session.business.inventory_on && session.permissions?.receive && !session.offline : n.owner ? isOwner : !n.perm || session.permissions?.[n.perm])).map(({ to, label, icon: Icon, id, premium, badge }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${id}`}
              title={label}
              className={({ isActive }) =>
                `relative flex h-10 min-w-[44px] shrink-0 items-center justify-center gap-2 rounded-lg px-2.5 text-sm font-semibold transition-colors 2xl:px-3 ${
                  isActive ? "bg-white/10 text-amber-400" : "text-slate-400 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <span className="relative"><Icon className="h-[18px] w-[18px]" />
                {badge && newOrders > 0 && <span className="absolute -right-2 -top-2 grid h-4 min-w-4 place-items-center rounded-full bg-amber-400 px-1 text-[10px] font-bold text-slate-950" data-testid="nav-online-orders-new">{newOrders}</span>}</span>
              <span className="hidden whitespace-nowrap 2xl:inline">{label}</span>
              {premium && <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-amber-400 2xl:hidden" data-testid={`nav-${id}-premium-dot`} />}
              {premium && <span className="hidden rounded bg-amber-400 px-1 text-[9px] font-bold uppercase text-slate-950 2xl:inline" data-testid={`nav-${id}-premium`}>Premium</span>}
            </NavLink>
          ))}
        </nav>
        <BranchSwitcher />
        {wide && <DemoCountdown demo={biz.demo} canUpgrade={isOwner} />}
        <div className="hidden min-w-0 max-w-[180px] text-right xl:block">
          <div className="truncate font-display text-sm font-semibold text-white" data-testid="header-business-name">{biz.name}</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400" data-testid="header-business-meta">
            {session.user.role_label} · {session.user.username || biz.currency}
          </div>
        </div>
        {!session.impersonation && !session.offline && <ScanLoginButton />}
        <button
          data-testid="logout-button"
          onClick={() => {
            signOut();
            nav("/login");
          }}
          className="grid h-10 w-10 shrink-0 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-white/10 hover:text-white"
          aria-label="Log out"
        >
          <LogOut className="h-[18px] w-[18px]" />
        </button>
      </header>
      {!wide && <DemoCountdown demo={biz.demo} compact canUpgrade={isOwner} />}
      <main key={session.branch.id} className="min-h-0 flex-1 overflow-hidden">{children}</main>
    </div>
  );
}
